import { useNavigate } from 'react-router-dom'
import { useToast } from '../../contexts/ToastContext'
import { clickTarget } from '../../utils/tutorialTargets'
import { TUTORIAL_COPY } from '../../constants/tutorialContent'
import { Button } from '../ui/button'

/**
 * TutorialCallout — one panel: chip, title, body, primary + skip.
 * One click skips immediately (PRD FR-4); a toast teaches recovery — no
 * guilt dialog (art spec §4).
 */

export default function TutorialCallout({
    step,
    beat,
    isLastBeat = false,
    style,
    centered = false,
    onNextBeat,
    onAck,
    onWait,
    onNavigate,
    onSkip,
}) {
    const navigate = useNavigate()
    const toast = useToast()

    const handleCta = async () => {
        const action = beat.cta?.action
        if (action === 'next-beat' || action === 'ack-beat') {
            // Local advance; the server step completes via its milestone (FR-2).
            onNextBeat()
            // Last beat of a waitsAfterBeats step: there is no next beat to
            // show — yield to the waiting pill while the player does the real
            // action. Without this the callout just sits there ("nothing
            // happens") because the beat pointer clamps to the last beat.
            if (isLastBeat && step.waitsAfterBeats) onWait?.()
            return
        }
        if (action === 'ack') {
            onAck()
            return
        }
        if (action === 'navigate') {
            onNextBeat() // advance to the next beat before yielding to the pill
            onNavigate?.() // yield — the spotlight returns on its own once the target view mounts
            navigate(beat.cta.to)
            return
        }
        if (action === 'click') {
            onWait()
            const ok = clickTarget(beat.target)
            if (!ok) toast.info(TUTORIAL_COPY.blockedClick)
            return
        }
        onNextBeat()
    }

    const handleSkip = () => {
        // Overlay hands us { skip, collapseEsc } (SpotlightLayer owns Esc); a
        // plain function is also accepted — normalize, then fire-and-toast.
        const skipFn = typeof onSkip === 'function' ? onSkip : onSkip?.skip
        skipFn?.()
        toast.info(TUTORIAL_COPY.skipToast)
    }

    const cls = [
        'tcd-callout',
        centered ? 'tcd-pos-center' : '',
    ].filter(Boolean).join(' ')

    return (
        <div
            className={cls}
            style={centered ? undefined : style}
            role="dialog"
            aria-label={`${TUTORIAL_COPY.logChip(step.id)} — ${beat.title}`}
            data-testid="tutorial-callout"
        >
            <div className="tcd-callout__chip">
                {`${TUTORIAL_COPY.logChip(step.id)} · ${beat.title}`}
            </div>
            <h2 className="tcd-callout__title">{beat.title}</h2>
            <p className="tcd-callout__body">{beat.body}</p>
            <div className="tcd-callout__actions">
                <Button size="sm" onClick={handleCta} data-testid="tutorial-cta">
                    {beat.cta?.label || 'Next'}
                </Button>
                <button type="button" className="tcd-callout__skip" onClick={handleSkip}>
                    {TUTORIAL_COPY.skip}
                </button>
            </div>
            {step.demo && step.real && (
                <div className="tcd-callout__chip" style={{ marginTop: 10 }}>
                    {`${step.demo} · ${step.real}`}
                </div>
            )}
        </div>
    )
}
